import React from "react";
import { localizeByKeyStatic } from "components/service";
import Icons from "components/icons";
import {
  formatCommunicationDateTime,
  formatRelativeCommunicationDate,
} from "../helpers/communication-date-helper";
import { getCommunicationOpenActionLabelKey } from "../helpers/communication-open-action-helper";
import {
  COMMUNICATION_PARSER_CONFIDENCE_HINT_KEY,
  COMMUNICATION_PARSER_CONFIDENCE_LABEL_KEY,
  getCommunicationConfidenceTone,
  normalizeCommunicationConfidence,
} from "../helpers/communication-confidence-helper";
import { formatCommunicationStandNumberLabel } from "../helpers/communication-stand-format-helper";
import CommunicationConst from "../communication-const";
import CommunicationNextStepField from "./communication-next-step-field";
import { getAvatarHue, getSenderDisplayLabel, getSenderInitials } from "./communication-info-field";
import type {
  TCommunicationTab,
  TCommunicationTask,
} from "../helpers/communication-types-helper";

interface ICommunicationReadingPaneProps {
  task: TCommunicationTask;
  currentTab: TCommunicationTab;
  onOpen: () => void;
  onClose: () => void;
}

const workStateLabelMap: Record<TCommunicationTask["work_state"]["status"], string> = {
  untouched: "COMMUNICATION.WorkState_Untouched",
  in_progress: "COMMUNICATION.WorkState_InProgress",
  approved: "COMMUNICATION.WorkState_Approved",
};

const getCountLabel = (
  count: number,
  singularKey: string,
  pluralKey: string
): string =>
  localizeByKeyStatic(count === 1 ? singularKey : pluralKey, { count });

const getStandNumberLabels = (task: TCommunicationTask): string[] => {
  const contextGroups = task.context_groups?.length
    ? task.context_groups
    : [];
  const standLabels = Array.from(
    new Set(
      (contextGroups.length ? contextGroups : task.request_groups)
        .map((group) => formatCommunicationStandNumberLabel(group.stand_number))
        .filter((label) => label && label !== "\u2014")
    )
  );

  if (standLabels.length > 0) {
    return standLabels;
  }

  return [ formatCommunicationStandNumberLabel(task.stand_number) ];
};

const getNextStepLabel = (task: TCommunicationTask): string => {
  if (task.status === CommunicationConst.statuses.UNIDENTIFIED) {
    return localizeByKeyStatic("COMMUNICATION.NextStep_Match");
  }

  if (task.status === CommunicationConst.statuses.PENDING) {
    return localizeByKeyStatic("COMMUNICATION.NextStep_Review");
  }

  return localizeByKeyStatic("COMMUNICATION.NextStep_History");
};

const CommunicationReadingPane: React.FC<ICommunicationReadingPaneProps> = ({
  task,
  currentTab,
  onOpen,
  onClose,
}) => {
  const [isChangeListExpanded, setIsChangeListExpanded] = React.useState(false);
  const confidence = normalizeCommunicationConfidence(
    task.ai_response_confidence ?? task.confidence
  );
  const confidenceTone = getCommunicationConfidenceTone(confidence);
  const parserConfidenceLabel = localizeByKeyStatic(COMMUNICATION_PARSER_CONFIDENCE_LABEL_KEY);
  const parserConfidenceHint = localizeByKeyStatic(COMMUNICATION_PARSER_CONFIDENCE_HINT_KEY);
  const senderDisplayLabel = getSenderDisplayLabel(task);
  const avatarSeed = task.sender || senderDisplayLabel;
  const avatarInitials = getSenderInitials(avatarSeed);
  const avatarHue = getAvatarHue(avatarSeed);
  const openQuestions = task.questions.filter((q) => q.status === "open").length;
  const totalQuestions = task.questions.length;
  const requestCount = task.request_items.length;
  const equipmentChanges = task.final_changes.equipment_changes;
  const canCollapseChanges = equipmentChanges.length > 8;
  const visibleChangeCount = canCollapseChanges && !isChangeListExpanded ? 6 : equipmentChanges.length;
  const previewChanges = equipmentChanges.slice(0, visibleChangeCount);
  const hiddenChangesCount = Math.max(0, equipmentChanges.length - visibleChangeCount);
  const contextGroups = task.context_groups?.length
    ? task.context_groups
    : [{
      id: `context-${task.id}-0`,
      event_name: task.event_name || "\u2014",
      company_name: task.company_name || "\u2014",
      stand_number: task.stand_number || "",
    }];
  const visibleContextGroups = contextGroups.slice(0, 2);
  const hiddenContextGroupsCount = Math.max(0, contextGroups.length - visibleContextGroups.length);
  const standLabels = getStandNumberLabels(task);
  const visibleStandLabels = standLabels.slice(0, 2);
  const hiddenStandCount = Math.max(0, standLabels.length - visibleStandLabels.length);
  const workStateLabel = localizeByKeyStatic(workStateLabelMap[ task.work_state.status ]);
  const shouldShowWorkState = task.work_state.status !== "untouched";
  const senderEmailHref = task.sender_email?.trim()
    ? `mailto:${task.sender_email.trim()}`
    : undefined;
  const contextSummary = visibleContextGroups
    .map((group) => [ group.company_name || "\u2014", formatCommunicationStandNumberLabel(group.stand_number) ].filter(Boolean).join(" \u2022 "))
    .join(" / ");
  const isDismissed = task.status === CommunicationConst.statuses.DISMISSED;
  const hasSpamSignal = !isDismissed && task.intent === "spam";
  const hasNeedsMatch = task.status === CommunicationConst.statuses.UNIDENTIFIED;
  const hasProblems = !isDismissed && (openQuestions > 0 || task.status === CommunicationConst.statuses.UNIDENTIFIED);
  const problemLabel = hasNeedsMatch
    ? localizeByKeyStatic("COMMUNICATION.Info_NeedsMatch")
    : getCountLabel(openQuestions, "COMMUNICATION.Info_OpenClarification", "COMMUNICATION.Info_OpenClarificationPlural");
  const dismissedReasonLabel = task.dismissed_reason
    ? localizeByKeyStatic(`COMMUNICATION.NotRelevantReason_${task.dismissed_reason}`)
    : "";
  const nextStepLabel = getNextStepLabel(task);
  const followUpLabel = openQuestions > 0
    ? localizeByKeyStatic("COMMUNICATION.NextAction_FollowUpNeeded")
    : localizeByKeyStatic("COMMUNICATION.NextAction_NoFollowUpNeeded");
  const aiParserLabel =
    task.ai_provider && task.ai_model
      ? `${task.ai_provider} / ${task.ai_model}`
      : task.ai_model || task.ai_provider || "";
  const selectedParserRunLabel = task.ai_attempt && task.ai_attempt > 0
    ? localizeByKeyStatic("COMMUNICATION.AiAttempt_Value", {
      count: task.ai_attempt,
    })
    : "\u2014";
  const hasAiSidebarDetails = Boolean(task.ai_selected_response_id || aiParserLabel);

  const snippet = (task.ai_summary?.trim() || task.original_body?.trim() || "")
    .replace(/\s+/g, " ")
    .trim();

  const openButtonLabel = localizeByKeyStatic(
    getCommunicationOpenActionLabelKey(task, currentTab)
  );

  const actionTag = (action: string) => {
    const map: Record<string, string> = {
      add: "COMMUNICATION.Action_Add",
      remove: "COMMUNICATION.Action_Remove",
      change: "COMMUNICATION.Action_Change",
    };
    return map[action] ? localizeByKeyStatic(map[action]) : action;
  };

  React.useEffect(() => {
    setIsChangeListExpanded(false);
  }, [ task.id ]);

  return (
    <div className="communication-reading-pane">
      <div className="communication-reading-pane__header">
        <div className="communication-reading-pane__header-copy">
          <span className="communication-reading-pane__subject" title={task.subject}>
            {task.subject || "\u2014"}
          </span>

          <div className="communication-reading-pane__sender-row">
            <span
              className="communication-reading-pane__avatar"
              style={{ "--avatar-hue": `${avatarHue}` } as React.CSSProperties}
            >
              {avatarInitials}
            </span>
            {senderEmailHref ? (
              <a
                className="communication-reading-pane__sender"
                href={senderEmailHref}
                title={task.sender}
              >
                {senderDisplayLabel}
              </a>
            ) : (
              <span className="communication-reading-pane__sender" title={task.sender}>
                {senderDisplayLabel}
              </span>
            )}
          </div>
        </div>

        <div className="communication-reading-pane__header-side">
          <span
            className={`communication-reading-pane__confidence communication-reading-pane__confidence--${confidenceTone}`}
            title={`${parserConfidenceLabel}: ${confidence}%. ${parserConfidenceHint}`}
          >
            {`${confidence}%`}
          </span>
          <button
            type="button"
            className="communication-reading-pane__close"
            onClick={onClose}
          >
            <Icons.Close />
          </button>
        </div>
      </div>

      <div className="communication-reading-pane__context-row">
        <span className="communication-reading-pane__context" title={contextSummary}>
          {contextSummary}
        </span>
      </div>

      {(task.demo_badge_key || shouldShowWorkState) && (
        <div className="communication-reading-pane__badges">
          {task.demo_badge_key && (
            <span className="communication-info-field__demo-badge">
              {localizeByKeyStatic(task.demo_badge_key)}
            </span>
          )}
          {shouldShowWorkState && (
            <span className={`communication-info-field__state communication-info-field__state--${task.work_state.status}`}>
              {workStateLabel}
            </span>
          )}
        </div>
      )}

      <div className="communication-reading-pane__chips">
        {isDismissed && (
          <span className="communication-info-field__chip communication-info-field__chip--dismissed">
            {localizeByKeyStatic("COMMUNICATION.Info_NotRelevant")}
          </span>
        )}
        {isDismissed && dismissedReasonLabel && (
          <span className="communication-info-field__chip communication-info-field__chip--muted">
            {dismissedReasonLabel}
          </span>
        )}
        {hasSpamSignal && (
          <span className="communication-info-field__chip communication-info-field__chip--spam">
            {localizeByKeyStatic("COMMUNICATION.Info_SpamSignal")}
          </span>
        )}
        {task.ai_needs_review && !hasNeedsMatch && (
          <span className="communication-info-field__chip communication-info-field__chip--problem">
            {localizeByKeyStatic("COMMUNICATION.AiReview_Required")}
          </span>
        )}
        {requestCount > 0 && (
          <span className="communication-info-field__chip communication-info-field__chip--orders">
            {getCountLabel(requestCount, "COMMUNICATION.Info_ItemsCount", "COMMUNICATION.Info_ItemsCountPlural")}
          </span>
        )}
        {totalQuestions > 0 && (
          <span className="communication-info-field__chip communication-info-field__chip--questions">
            {getCountLabel(totalQuestions, "COMMUNICATION.Info_ChipQuestionsSingle", "COMMUNICATION.Info_ChipQuestions")}
          </span>
        )}
        {hasProblems && (
          <span className="communication-info-field__chip communication-info-field__chip--problem">
            {problemLabel}
          </span>
        )}
      </div>

      <div className="communication-reading-pane__detail-grid">
        <div className="communication-reading-pane__detail-card">
          <span className="communication-reading-pane__detail-label">
            {localizeByKeyStatic("COMMUNICATION.Table_EventCompany")}
          </span>
          <div className="communication-reading-pane__context-list">
            {visibleContextGroups.map((group) => (
              <div key={group.id} className="communication-reading-pane__context-item">
                <strong className="communication-reading-pane__detail-primary">
                  {group.event_name || "\u2014"}
                </strong>
                <span className="communication-reading-pane__detail-secondary">
                  {group.company_name || "\u2014"}
                </span>
              </div>
            ))}
            {hiddenContextGroupsCount > 0 && (
              <span className="communication-reading-pane__detail-secondary">
                {`+${hiddenContextGroupsCount} more`}
              </span>
            )}
          </div>
        </div>

        <div className="communication-reading-pane__detail-card">
          <span className="communication-reading-pane__detail-label">
            {localizeByKeyStatic("COMMUNICATION.Table_Stand")}
          </span>
          <div className="communication-reading-pane__stand-list">
            {visibleStandLabels.map((standLabel) => (
              <span key={standLabel} className="communication-reading-pane__stand-value">
                {standLabel}
              </span>
            ))}
            {hiddenStandCount > 0 && (
              <span className="communication-table__stand-more">{`+${hiddenStandCount}`}</span>
            )}
          </div>
          {standLabels.length > 1 && (
            <span className="communication-table__stand-badge">
              {getCountLabel(
                standLabels.length,
                "COMMUNICATION.Info_StandGroupsCount",
                "COMMUNICATION.Info_StandGroupsCountPlural"
              )}
            </span>
          )}
        </div>

        <div className="communication-reading-pane__detail-card">
          <span className="communication-reading-pane__detail-label">
            {localizeByKeyStatic("COMMUNICATION.Table_Received")}
          </span>
          <span className="communication-reading-pane__detail-primary">
            {formatRelativeCommunicationDate(task.received_at) || "\u2014"}
          </span>
          <span className="communication-reading-pane__detail-secondary">
            {formatCommunicationDateTime(task.received_at)}
          </span>
        </div>

        <div className="communication-reading-pane__detail-card">
          <span className="communication-reading-pane__detail-label">
            {localizeByKeyStatic("COMMUNICATION.Table_NextStep")}
          </span>
          <div className="communication-reading-pane__detail-step">
            <CommunicationNextStepField task={task} />
          </div>
        </div>

        <div className="communication-reading-pane__detail-card">
          <span className="communication-reading-pane__detail-label">
            {localizeByKeyStatic("COMMUNICATION.ReviewSummary_Label")}
          </span>
          <span className="communication-reading-pane__detail-primary">
            {localizeByKeyStatic("COMMUNICATION.Text_ChangesCount", {
              count: equipmentChanges.length,
            })}
          </span>
          <span className="communication-reading-pane__detail-secondary">
            {totalQuestions > 0
              ? localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                open: openQuestions,
                questions: totalQuestions,
              })
              : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions")}
          </span>
          <span className="communication-reading-pane__detail-secondary">
            {`${localizeByKeyStatic("COMMUNICATION.Table_NextStep")}: ${nextStepLabel}`}
          </span>
          <span className="communication-reading-pane__detail-secondary">
            {task.ai_needs_review
              ? localizeByKeyStatic("COMMUNICATION.AiReview_Required")
              : followUpLabel}
          </span>
        </div>

        {hasAiSidebarDetails && (
          <div className="communication-reading-pane__detail-card">
            <span className="communication-reading-pane__detail-label">
              {localizeByKeyStatic("COMMUNICATION.AiParser_Label")}
            </span>
            <span className="communication-reading-pane__detail-primary">
              {aiParserLabel || "\u2014"}
            </span>
            <span className="communication-reading-pane__detail-secondary">
              {`${localizeByKeyStatic("COMMUNICATION.AiParser_Selected")}: ${selectedParserRunLabel}`}
            </span>
          </div>
        )}
      </div>

      {snippet && (
        <p className="communication-reading-pane__snippet">{snippet}</p>
      )}

      {equipmentChanges.length > 0 && (
        <div className="communication-reading-pane__section">
          <div className="communication-reading-pane__section-head">
            <span className="communication-reading-pane__section-label">
              {localizeByKeyStatic("COMMUNICATION.Table_Changes")}
            </span>
            <span className="communication-info-field__chip communication-info-field__chip--orders">
              {`${equipmentChanges.length}`}
            </span>
          </div>
          <ul className="communication-reading-pane__change-list">
            {previewChanges.map((change, idx) => (
              <li key={change.id || idx} className="communication-reading-pane__change-item">
                <span className={`communication-reading-pane__action communication-reading-pane__action--${change.action}`}>
                  {actionTag(change.action)}
                </span>
                <span className="communication-reading-pane__change-label">
                  {change.equipment_title || change.item_label || "\u2014"}
                  {change.qty > 1 ? ` \u00d7${change.qty}` : ""}
                </span>
              </li>
            ))}
            {hiddenChangesCount > 0 && !canCollapseChanges && (
              <li className="communication-reading-pane__change-more">
                {`+${hiddenChangesCount}`}
              </li>
            )}
          </ul>
          {canCollapseChanges && (
            <button
              type="button"
              className="communication-reading-pane__change-toggle"
              onClick={() => setIsChangeListExpanded((current) => !current)}
            >
              {isChangeListExpanded
                ? localizeByKeyStatic("COMMUNICATION.Button_CollapseChanges")
                : localizeByKeyStatic("COMMUNICATION.Button_ShowAllChanges", {
                  count: equipmentChanges.length,
                })}
            </button>
          )}
        </div>
      )}

      {totalQuestions > 0 && (
        <div className="communication-reading-pane__section">
          <div className="communication-reading-pane__section-head">
            <span className="communication-reading-pane__section-label">
              {localizeByKeyStatic("COMMUNICATION.Table_Questions")}
            </span>
            <span className={`communication-reading-pane__questions${openQuestions > 0 ? " communication-reading-pane__questions--open" : ""}`}>
              {localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                open: openQuestions,
                questions: totalQuestions,
              })}
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        className="communication-reading-pane__open-btn"
        onClick={onOpen}
      >
        {openButtonLabel}
      </button>
    </div>
  );
};

export default CommunicationReadingPane;
